import type { DepositMergePlan } from "./depositMergePlan";

export type MergePlan = Extract<DepositMergePlan, { mode: "merge" }>;
export type DepositMergeStage =
  | "planned"
  | "prepared"
  | "backed-up"
  | "released"
  | "submitted"
  | "finalized";

/** Carries no witness, balance, proof or signing response in its message. */
export class DepositMergeRecoveryRequired extends Error {
  constructor(
    readonly stage: DepositMergeStage,
    readonly journalId?: string,
  ) {
    super(
      "Deposit status needs reconciliation before another deposit is submitted",
    );
    this.name = "DepositMergeRecoveryRequired";
  }
}

function checkSignal(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw new DOMException("Deposit cancelled", "AbortError");
  }
}

/**
 * Chain adapters supply the existing lock, encrypted journal and receipt
 * reconciliation. This module defines ordering; it never handles private keys.
 * A released proof is never followed by an automatic replacement deposit.
 */
export async function runDepositMergeWorkflow<
  Prepared,
  Submitted,
  Result,
>(params: {
  plan: DepositMergePlan;
  signal?: AbortSignal;
  ordinaryDeposit: () => Promise<Result>;
  withWalletLock: <T>(run: () => Promise<T>) => Promise<T>;
  /** Revalidate chain, owner, input, quote and reservations under the lock. */
  revalidate: (plan: MergePlan) => Promise<void>;
  /** Client-side generation only; do not send private witness data to a service. */
  prepareLocally: (plan: MergePlan) => Promise<Prepared>;
  /** Must resolve only after durable, content-bound encrypted backup acknowledgement. */
  backupEncrypted: (prepared: Prepared) => Promise<string>;
  /** Journal the release BEFORE remote simulation or fee authorization sees the proof. */
  markReleased: (journalId: string) => Promise<void>;
  preflight: (prepared: Prepared) => Promise<void>;
  signAndSubmit: (prepared: Prepared, journalId: string) => Promise<Submitted>;
  reconcile: (
    submitted: Submitted,
    prepared: Prepared,
    journalId: string,
  ) => Promise<Result>;
}): Promise<Result> {
  checkSignal(params.signal);
  if (params.plan.mode === "ordinary") return params.ordinaryDeposit();
  const plan = params.plan;
  return params.withWalletLock(async () => {
    let stage: DepositMergeStage = "planned";
    let journalId: string | undefined;
    try {
      checkSignal(params.signal);
      await params.revalidate(plan);
      checkSignal(params.signal);
      const prepared = await params.prepareLocally(plan);
      stage = "prepared";
      checkSignal(params.signal);
      journalId = await params.backupEncrypted(prepared);
      if (!journalId) {
        throw new Error("Encrypted deposit backup was not acknowledged");
      }
      stage = "backed-up";
      checkSignal(params.signal);
      await params.revalidate(plan);
      checkSignal(params.signal);
      await params.markReleased(journalId);
      stage = "released";
      await params.preflight(prepared);
      checkSignal(params.signal);
      const submitted = await params.signAndSubmit(prepared, journalId);
      stage = "submitted";
      // Reconciliation remains necessary after submission even if the caller
      // closed the modal. Its chain adapter owns timeout and durable recovery.
      const result = await params.reconcile(submitted, prepared, journalId);
      stage = "finalized";
      return result;
    } catch (error) {
      if (stage === "released" || stage === "submitted") {
        throw new DepositMergeRecoveryRequired(stage, journalId);
      }
      throw error;
    }
  });
}
